<template>
  <div class="x-tinymce" :style="{height}" :class="{fullscreen: fullscreen}">
    <textarea class="x-tinymce-textarea" :id="tinymceId"></textarea>
    <div class="x-tinymce-btn-container">
      <el-button v-if="imageButtonVisible" @click="imageDialogVisible=true" :disabled="disabled" type="primary">上传图片</el-button>
      <el-button v-if="posterButtonVisible" @click="showPosterTemplate" :disabled="disabled" type="primary">选择海报</el-button>
    </div>
    <x-upload-dialog :action='$attrs.action' :visible.sync="imageDialogVisible" title="上传图片" :accept="acceptImage" :before-upload="handleBeforeUpload" :convert-upload-files="handleImageSubmit"></x-upload-dialog>
  </div>
</template>

<script>
  import plugins from './plugins';
  import toolbar from './toolbar';
  import {ACCEPT_IMAGE} from '@/util/x-condition-constant';
  export const IMAGE = 'image';
  export const POSTER = 'poster';
  export default {
    name: 'XTinymce',
    props: {
      buttonConfig: {
        type: Object,
        default() {
          return {
            list: [
              {
                type: IMAGE,
                show: false
              },
              {
                type: POSTER,
                show: false
              }
            ]
          };
        }
      },
      id: {
        type: String
      },
      value: {
        type: String,
        default: ''
      },
      autoResize: {
        type: Boolean,
        default: false
      },
      toolbar: Array,
      plugins: Array,
      menubar: {
        default: 'edit insert view format table'
      },
      height: {
        type: [Number, String],
        required: false,
        default: 360
      },
      minHeight: {
        type: Number,
        required: false,
        default: 0
      },
      disabled: Boolean,
      selectPosterTemplate: Function
    },
    data () {
      return {
        acceptImage: ACCEPT_IMAGE,
        fullscreen: false,
        imageDialogVisible: false,
        posterTemplateListVisible: false
      };
    },
    watch: {
      value (val) {
        this.$nextTick(() => {
          const tinymce = window.tinymce.get(this.tinymceId);
          if (!tinymce) {
            return;
          }
          const content = tinymce.getContent();
          if (content === val) {
            return;
          }
          tinymce.setContent(val || '');
        });
      }
    },
    computed: {
      tinymceId() {
        return this.id || 'vue-tinymce-' + +new Date();
      },
      imageButtonVisible() {
        if (!this.buttonConfig || !Array.isArray(this.buttonConfig.list)) {
          return false;
        }
        const list = this.buttonConfig.list;
        for (const item of list) {
          if (item.type === IMAGE) {
            if (Object.prototype.hasOwnProperty.call(item, 'show')) {
              return item.show;
            }
            return true;
          }
        }
        return false;
      },
      posterButtonVisible() {
        if (!this.buttonConfig || !Array.isArray(this.buttonConfig.list)) {
          return false;
        }
        const list = this.buttonConfig.list;
        for (const item of list) {
          if (item.type === POSTER) {
            if (Object.prototype.hasOwnProperty.call(item, 'show')) {
              return item.show;
            }
            return true;
          }
        }
        return false;
      }
    },
    methods: {
      handleBeforeUpload(file) {
        const arr = file.name.split('.');
        const suffix = arr[arr.length - 1].toLowerCase();
        const uploadFileType = [];
        const acceptArr = this.acceptImage.split(',');
        for (const acceptItem of acceptArr) {
          uploadFileType.push(acceptItem.split('.')[1]);
        }
        if (uploadFileType.indexOf(suffix) < 0) {
          this.$notify.error(`只能上传${uploadFileType.join(',')}格式的文件`);
          return false;
        }
        return true;
      },
      handleClick({item}) {
        if (this.selectPosterTemplate) {
          this.selectPosterTemplate({item});
        }
      },
      showPosterTemplate() {
        this.posterTemplateListVisible = true;
      },
      initTinymce () {
        if (window.tinymce.get(this.tinymceId)) {
          return;
        }
        window.tinymce.init({
          language: 'zh_CN',
          selector: `#${this.tinymceId}`,
          height: this.height,
          relative_urls: false,
          body_class: 'panel-body ',
          object_resizing: false,
          toolbar: this.toolbar ? this.toolbar : toolbar,
          menubar: this.menubar,
          plugins: this.plugins ? this.plugins : plugins,
          min_height: this.minHeight,
          readonly: this.disabled,
          end_container_on_empty_block: true,
          powerpaste_word_import: 'clean',
          code_dialog_height: 450,
          code_dialog_width: 1000,
          advlist_bullet_styles: 'square',
          advlist_number_styles: 'default',
          imagetools_cors_hosts: ['www.tinymce.com', 'codepen.io'],
          default_link_target: '_blank',
          link_title: false,
          nonbreaking_force_tab: true, // inserting nonbreaking space &nbsp; need Nonbreaking Space Plugin
          init_instance_callback: editor => {
            if (this.value) {
              editor.setContent(this.value);
            }
            editor.on('NodeChange Change KeyUp SetContent', () => {
              this.$emit('input', editor.getContent());
            });
          },
          setup: (editor) => {
            editor.on('FullscreenStateChanged', (e) => {
              this.fullscreen = e.state;
            });
          }
        });
      },
      destroyTinymce () {
        const tinymceInstance = window.tinymce.get(this.tinymceId);
        if (tinymceInstance) {
          tinymceInstance.destroy();
        }
      },
      setContent (value) {
        window.tinymce.get(this.tinymceId).setContent(value);
      },
      getContent () {
        window.tinymce.get(this.tinymceId).getContent();
      },
      handleImageSubmit ({uploadFiles}) {
        uploadFiles.forEach(v => {
          window.tinymce.get(this.tinymceId).insertContent(`<img alt="" class="wscnph" src="${v.response.data.url}" >`);
        });
      }
    },
    mounted () {
      // keep-alive情况下，切换页面会导致tinymce渲染问题，需要在多个钩子函数中初始化和销毁实例
      this.$nextTick(() => {
        this.initTinymce();
      });
    },
    activated () {
      // keep-alive情况下，切换页面会导致tinymce渲染问题，需要在多个钩子函数中初始化和销毁实例
      this.$nextTick(() => {
        this.initTinymce();
      });
    },
    deactivated () {
      // keep-alive情况下，切换页面会导致tinymce渲染问题，需要在多个钩子函数中初始化和销毁实例
      this.destroyTinymce();
    },
    destroyed () {
      // keep-alive情况下，切换页面会导致tinymce渲染问题，需要在多个钩子函数中初始化和销毁实例
      this.destroyTinymce();
    }
  };
</script>
<style lang="scss">
.x-tinymce {
  position: relative;
  .x-tinymce-btn-container {
    position: absolute;
    right: 4px;
    top: 4px;
    z-index: 1;
  }
  &.fullscreen {
    .x-tinymce-btn-container {
      position: fixed;
      z-index: 1201;
    }
  }
}
.x-tinymce-image-upload {
  .el-upload-list--picture-card {
    /*修复添加图片后父元素高度改变的bug*/
    font-size: 0;
  }
  .el-upload--picture-card {
    margin: 0 8px 8px 0;
  }
}
.x-tinymce-image-upload-hint {
  color: red;
}
.x-tinymce-image-upload-btn-container {
  text-align: right;
  margin-top: 10px;
}
</style>